import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BaseSelector from '@/components/selectors/BaseSelector';
import { ModuleData, ModuleType } from '@/components/filters/types';
import { useStore } from '@core/store';

interface ModuleSelectorProps {
    value: string | null;
    onChange: (moduleId: string | null) => void;
    shopId: string | null;
    moduleType: ModuleType;
    disabled?: boolean;
    label?: string;
    modules?: any[];
    autoSelectFirst?: boolean;
}

const ModuleSelector = ({
    value,
    onChange,
    shopId,
    moduleType,
    disabled = false,
    label,
    modules,
    autoSelectFirst = false
}: ModuleSelectorProps) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const componentIds = useStore((state) => state.componentIds);
    const loadComponentIds = useStore((state) => state.loadComponentIds);

    useEffect(() => {
        if (modules && modules.length > 0) return;

        let isCancelled = false;

        const fetchData = async () => {
            if (!componentIds || componentIds.length === 0) {
                setIsLoading(true);
                try {
                    console.log('Loading component IDs again...');
                    await loadComponentIds();
                } catch (error) {
                    console.error('Failed to load components:', error);
                } finally {
                    if (!isCancelled) setIsLoading(false);
                }
            }
        };

        fetchData();
        return () => {
            isCancelled = true;
        };
    }, [modules, componentIds.length, loadComponentIds]);

    const filteredModules = useMemo(() => {
        if (!shopId || !componentIds) return [];
        if (modules && modules.length > 0) return modules;

        const normalizedType = moduleType?.toLowerCase() || '';

        return componentIds
            .filter((component) => {
                if (!component || component.shopId !== shopId) return false;
                if (component.disabled || component.deleteDate) return false;

                const disc = (component.discriminator || '').toLowerCase();
                const type = (component.type || '').toLowerCase();

                return disc === normalizedType || type.includes(normalizedType);
            })
            .map((component) => ({
                id: component.id,
                shopId: component.shopId,
                deviceId: component.deviceId,
                deleteDate: component.deleteDate,
                disabled: component.disabled
            })) as ModuleData[];
    }, [shopId, moduleType, componentIds, modules]);

    useEffect(() => {
        if (autoSelectFirst && filteredModules.length > 0 && !value && shopId) {
            onChange(filteredModules[0].id);
        }
    }, [autoSelectFirst, filteredModules, value, shopId, onChange]);

    const items = filteredModules.map((m) => ({ value: m.id, text: m.id }));

    return (
        <BaseSelector
            value={value}
            items={items}
            itemText="text"
            itemValue="value"
            label={label ?? t(moduleType)}
            disabled={disabled || isLoading || !shopId}
            onChange={(val) => onChange(val ?? null)}
        />
    );
};

export default ModuleSelector;
